// Validation utilities for authentication forms
// Matches cleancy mobile app validation logic

export interface LoginFormData {
  email?: string;
  emailOrNickname?: string;
  password?: string;
}

export interface SignupFormData {
  nickname?: string;
  email?: string;
  password?: string;
  fullname?: string;
  phone?: string;
}

/**
 * Convert a Russian mobile number entered with spaces/punctuation to the
 * canonical value accepted by the API. An initial 8 is treated as the common
 * domestic spelling of the +7 country code.
 */
const getRussianNationalDigits = (phone: string): string => {
  const trimmedPhone = phone.trim();
  const digits = trimmedPhone.replace(/\D/g, "");

  // The controlled input retains its visible +7 prefix while the user types.
  // Strip that prefix independently of length so a partial number can never
  // be reinterpreted as a complete ten-digit national number.
  if (trimmedPhone.startsWith("+7")) {
    return digits.slice(1, 11);
  }

  // Accept complete numbers pasted in either 7XXXXXXXXXX or 8XXXXXXXXXX form.
  // A bare ten-digit value is already the national part.
  if (digits.length >= 11 && (digits.startsWith("7") || digits.startsWith("8"))) {
    return digits.slice(1, 11);
  }

  return digits.slice(0, 10);
};

export const normalizeRussianPhone = (phone: string): string =>
  `+7${getRussianNationalDigits(phone)}`;

export const formatRussianPhone = (phone: string): string => {
  const nationalDigits = getRussianNationalDigits(phone);

  const groups = [
    nationalDigits.slice(0, 3),
    nationalDigits.slice(3, 6),
    nationalDigits.slice(6, 8),
    nationalDigits.slice(8, 10),
  ].filter(Boolean);

  return groups.length ? `+7 ${groups.join(" ")}` : "+7";
};

export const validateRussianPhone = (phone: string): { valid: boolean; error?: string } => {
  if (!/^\+79\d{9}$/.test(normalizeRussianPhone(phone))) {
    return { valid: false, error: "Enter a Russian mobile number in +79 format" };
  }

  return { valid: true };
};

export const validateNickname = (nickname: string): { valid: boolean; error?: string } => {
  if (!nickname) {
    return { valid: false, error: 'Nickname is required' };
  }
  if (!/^[a-zA-Z0-9_-]+$/.test(nickname)) {
    return { valid: false, error: 'Nickname can only contain letters, numbers, hyphens, and underscores' };
  }
  if (nickname.length < 3) {
    return { valid: false, error: 'Nickname must be at least 3 characters long' };
  }
  if (nickname.length > 25) {
    return { valid: false, error: 'Nickname must be at most 25 characters long' };
  }
  return { valid: true };
};

export const validateEmail = (email: string): { valid: boolean; error?: string } => {
  if (!email) {
    return { valid: false, error: 'Email is required' };
  }
  if (!email.match(/^\S+@\S+\.\S{2,}$/)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }
  if (email.length > 64) {
    return { valid: false, error: 'Email must be at most 64 characters long' };
  }
  return { valid: true };
};

export const validatePassword = (password: string): { valid: boolean; error?: string } => {
  if (!password) {
    return { valid: false, error: 'Password is required' };
  }
  if (password.length < 6) {
    return { valid: false, error: 'Password must be at least 6 characters long' };
  }
  if (password.length > 64) {
    return { valid: false, error: 'Password must be at most 64 characters long' };
  }
  return { valid: true };
};

export const validateFullname = (fullname: string): { valid: boolean; error?: string } => {
  if (!fullname.trim()) {
    return { valid: false, error: 'Full name is required' };
  }
  if (fullname.trim().length > 128) {
    return { valid: false, error: 'Full name must be at most 128 characters long' };
  }
  return { valid: true };
};

export const validateLoginForm = (loginData: LoginFormData): { valid: boolean; error?: string } => {
  // Support both new 'emailOrNickname' field and legacy 'email' field
  const identifier = loginData.emailOrNickname || loginData.email;

  if (!identifier) {
    return { valid: false, error: 'Email or nickname is required' };
  }

  // Check if it's an email (contains @ and domain)
  const isEmail = identifier.includes('@') && identifier.match(/^\S+@\S+\.\S{2,}$/);

  if (isEmail) {
    const emailValidation = validateEmail(identifier);
    if (!emailValidation.valid) {
      return emailValidation;
    }
  } else {
    // Validate as nickname
    const nicknameValidation = validateNickname(identifier);
    if (!nicknameValidation.valid) {
      return nicknameValidation;
    }
  }

  // Validate password
  const passwordValidation = validatePassword(loginData.password || '');
  if (!passwordValidation.valid) {
    return passwordValidation;
  }

  return { valid: true };
};

export const validateSignupForm = (
  signupData: SignupFormData,
  confirmPassword?: string
): { valid: boolean; error?: string } => {
  // Validate nickname
  const nicknameValidation = validateNickname(signupData.nickname || '');
  if (!nicknameValidation.valid) {
    return nicknameValidation;
  }

  // Validate email
  const emailValidation = validateEmail(signupData.email || '');
  if (!emailValidation.valid) {
    return emailValidation;
  }

  // A verified Russian mobile number is required by the registration API.
  const phoneValidation = validateRussianPhone(signupData.phone || '');
  if (!phoneValidation.valid) {
    return phoneValidation;
  }

  // Validate password
  const passwordValidation = validatePassword(signupData.password || '');
  if (!passwordValidation.valid) {
    return passwordValidation;
  }

  // Validate confirm password if provided
  if (confirmPassword !== undefined && signupData.password !== confirmPassword) {
    return { valid: false, error: 'Passwords do not match' };
  }

  // Full name is required by the registration contract.
  const fullnameValidation = validateFullname(signupData.fullname || '');
  if (!fullnameValidation.valid) {
    return fullnameValidation;
  }

  return { valid: true };
};
